import React from "react";
import { X, AlertCircle, FileText } from "lucide-react";

interface BulkSendError {
  leadId: string;
  error: string;
  orderId?: number | null;
}

interface BulkSendErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: BulkSendError[];
  sent: number;
  total: number;
}

const BulkSendErrorModal: React.FC<BulkSendErrorModalProps> = ({
  isOpen,
  onClose,
  errors,
  sent,
  total,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ marginTop: 0 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
            Bulk Send to Call Center - Error Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              <strong className="text-green-600">{sent}</strong> lead(s) sent successfully,{" "}
              <strong className="text-red-600">{errors.length}</strong> error(s) out of{" "}
              <strong>{total}</strong> total lead(s)
            </p>
          </div>

          <div className="space-y-4">
            {errors.map((error, idx) => (
              <div
                key={idx}
                className="border border-red-200 rounded-md p-4 bg-red-50"
              >
                <div className="flex items-start">
                  <FileText className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-red-900">
                          Lead ID: {error.leadId.substring(0, 8)}...
                        </span>
                        {error.orderId && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            Order ID: {error.orderId}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-red-800 font-medium mb-1">Error:</p>
                      <p className="text-sm text-red-700 bg-red-100 p-2 rounded border border-red-200 break-words">
                        {error.error}
                      </p>
                    </div>
                    <div className="mt-2 text-xs text-gray-600 font-mono">
                      Full Lead ID: {error.leadId}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1a3329]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkSendErrorModal;
